<template>
  <div class="nhlc-card" :style="teamAccentStyle">
    <div class="nhlc-top-row">
      <span v-if="game.importanceLabel || game.roundLabel" class="nhlc-context-badge">
        <i v-if="game.postseason" class="fa fa-trophy" aria-hidden="true"></i>
        {{ game.importanceLabel || game.roundLabel }}
      </span>
      <span class="nhlc-status-badge" :class="statusClass">
        <span v-if="isLive" class="nhlc-live-dot"></span>
        {{ game.statusDisplay || game.statusType }}
      </span>
    </div>

    <div class="nhlc-score-stack">
      <div class="nhlc-team-row" :class="{ 'nhlc-team-row--winner': isWinner('away'), 'nhlc-team-row--loser': isFinal && !isWinner('away') }">
        <div class="nhlc-team-main">
          <img
            v-if="game.awayTeam?.logoUrl"
            :src="game.awayTeam.logoUrl"
            :alt="`${game.awayTeam.fullName || game.awayTeam.name} logo`"
            class="nhlc-logo"
            width="28"
            height="28"
          />
          <span v-else class="nhlc-logo-fallback" aria-hidden="true">
            {{ game.awayTeam?.espnAbbreviation?.slice(0, 1) || 'A' }}
          </span>
          <div class="nhlc-team-details">
            <div class="nhlc-team-name-row">
              <span class="nhlc-team-name">{{ game.awayTeam?.name || 'Away' }}</span>
              <span v-if="isFinal && isWinner('away')" class="nhlc-win-mark" aria-label="Winner">✓</span>
            </div>
            <span v-if="game.awayTeam?.record" class="nhlc-record">{{ game.awayTeam.record }}</span>
          </div>
        </div>
        <div v-if="isFinal || isLive" class="nhlc-score" :class="{ 'nhlc-score--winner': isWinner('away') }">
          {{ awayScoreDisplay }}
        </div>
      </div>

      <div class="nhlc-team-row" :class="{ 'nhlc-team-row--winner': isWinner('home'), 'nhlc-team-row--loser': isFinal && !isWinner('home') }">
        <div class="nhlc-team-main">
          <img
            v-if="game.homeTeam?.logoUrl"
            :src="game.homeTeam.logoUrl"
            :alt="`${game.homeTeam.fullName || game.homeTeam.name} logo`"
            class="nhlc-logo"
            width="28"
            height="28"
          />
          <span v-else class="nhlc-logo-fallback" aria-hidden="true">
            {{ game.homeTeam?.espnAbbreviation?.slice(0, 1) || 'H' }}
          </span>
          <div class="nhlc-team-details">
            <div class="nhlc-team-name-row">
              <span class="nhlc-team-name">{{ game.homeTeam?.name || 'Home' }}</span>
              <span v-if="isFinal && isWinner('home')" class="nhlc-win-mark" aria-label="Winner">✓</span>
            </div>
            <span v-if="game.homeTeam?.record" class="nhlc-record">{{ game.homeTeam.record }}</span>
          </div>
        </div>
        <div v-if="isFinal || isLive" class="nhlc-score" :class="{ 'nhlc-score--winner': isWinner('home') }">
          {{ homeScoreDisplay }}
        </div>
      </div>
    </div>

    <div class="nhlc-game-meta">
      <span v-if="isPre" class="nhlc-tipoff">
        <i class="fa fa-clock" aria-hidden="true"></i> {{ tipoffDisplay }}
      </span>
      <span v-else-if="isLive" class="nhlc-clock">
        <i class="fa fa-bolt" aria-hidden="true"></i> {{ clockDisplay }}
      </span>
      <span v-else-if="marginValue === 1" class="nhlc-margin-badge">1-goal game</span>
      <span v-else-if="shootoutValue" class="nhlc-margin-badge nhlc-margin-badge--so">SO</span>
      <span v-else-if="overtimeValue" class="nhlc-margin-badge nhlc-margin-badge--ot">OT</span>
      <span v-if="game.broadcast" class="nhlc-broadcast">
        <i class="fa fa-tv" aria-hidden="true"></i> {{ game.broadcast }}
      </span>
    </div>

    <div v-if="game.seriesSummary" class="nhlc-series-strip">
      <i class="fa fa-chart-line" aria-hidden="true"></i>
      {{ game.seriesSummary }}
    </div>

    <div
      v-if="game.gameSummary && (isFinal || isLive)"
      class="nhlc-narrative"
      :class="{ 'nhlc-narrative--live': isLive }"
    >
      <i :class="isLive ? 'fa fa-bolt' : 'fa fa-lightbulb'" aria-hidden="true"></i>
      {{ game.gameSummary }}
    </div>

    <div v-if="showEnrichment" class="nhlc-enrichment">
      <span v-if="leadPerformer" class="nhlc-pill nhlc-pill--player">
        <i class="fa fa-star" aria-hidden="true"></i>
        {{ leadPerformer.playerName }}
        <strong>{{ leadPerformer.statLine }}</strong>
      </span>
      <span
        v-for="(stat, i) in previewStats"
        :key="i"
        class="nhlc-pill nhlc-pill--stat"
        :title="stat.description || undefined"
      >
        {{ stat.label }}
        <strong v-if="stat.value">{{ stat.value }}</strong>
      </span>
    </div>

    <div v-if="gameLink" class="nhlc-ctas">
      <router-link
        class="nhlc-btn nhlc-btn--primary"
        :class="{ 'nhlc-btn--live': isLive }"
        :to="gameLink"
        :aria-label="ctaLabel"
      >
        <i class="fa fa-table-list" aria-hidden="true"></i>
        {{ ctaText }}
      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NhlGameCard',
  props: {
    game: {
      type: Object,
      required: true,
    },
  },
  computed: {
    statusType() {
      return (this.game?.statusType || '').toUpperCase();
    },
    isFinal() { return this.statusType === 'FINAL'; },
    isLive() { return this.statusType === 'IN' || this.statusType === 'LIVE'; },
    isPre() { return !this.isFinal && !this.isLive; },
    statusClass() {
      if (this.isFinal) return 'status--final';
      if (this.isLive) return 'status--live';
      return 'status--pre';
    },
    awayAbbr() {
      return this.game?.awayTeam?.espnAbbreviation || this.game?.score?.awayTeam?.abbreviation || '';
    },
    homeAbbr() {
      return this.game?.homeTeam?.espnAbbreviation || this.game?.score?.homeTeam?.abbreviation || '';
    },
    awayScoreDisplay() {
      return this.game?.score?.awayScore ?? this.game?.score?.awayTeam?.score ?? '–';
    },
    homeScoreDisplay() {
      return this.game?.score?.homeScore ?? this.game?.score?.homeTeam?.score ?? '–';
    },
    marginValue() {
      return this.game?.score?.margin ?? null;
    },
    overtimeValue() {
      return this.game?.score?.overtime ?? this.game?.overtime ?? null;
    },
    shootoutValue() {
      return this.game?.score?.shootout ?? this.game?.shootout ?? null;
    },
    gameLink() {
      const url = this.game?.boxScoreUrl;
      if (typeof url !== 'string') return null;
      const path = url.trim();
      if (!path.startsWith('/sports/nhl/')) return null;
      return path;
    },
    ctaText() {
      if (this.isLive) return 'View Live Box Score';
      if (this.isFinal) return 'View Box Score';
      return 'Game Center';
    },
    ctaLabel() {
      const away = this.game?.awayTeam?.fullName || this.game?.awayTeam?.name || 'Away';
      const home = this.game?.homeTeam?.fullName || this.game?.homeTeam?.name || 'Home';
      return `${this.ctaText}: ${away} at ${home}`;
    },
    teamAccentStyle() {
      const away = this.normalizeColor(this.game?.awayTeam?.primaryColor);
      const home = this.normalizeColor(this.game?.homeTeam?.primaryColor);
      const winner = this.game?.score?.winner === this.homeAbbr ? home : away;
      return {
        '--away-accent': away,
        '--home-accent': home,
        '--winner-accent': winner,
      };
    },
    tipoffDisplay() {
      const dt = this.game?.gameDateTime;
      if (!dt) return 'TBD';
      try {
        return new Date(dt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
      } catch {
        return 'TBD';
      }
    },
    clockDisplay() {
      if (this.shootoutValue) return 'SO';
      const p = Number(this.game?.period);
      const c = this.game?.clock;
      if (!p) return 'Live';
      const label = p <= 3 ? `P${p}` : 'OT';
      return c ? `${label} ${c}` : label;
    },
    leadPerformer() {
      const list = Array.isArray(this.game?.topPerformers) ? this.game.topPerformers : [];
      return list[0] || null;
    },
    previewStats() {
      // nhl-v2 card rows are `{ label, value: "away-home", description }`.
      // Older cached rows carry a tricode value; they are not rendered.
      const list = Array.isArray(this.game?.notableStats) ? this.game.notableStats : [];
      return list
        .filter((stat) => stat && typeof stat.label === 'string' && stat.label.trim())
        .map((stat) => ({
          label: stat.label.trim(),
          value: stat.value != null ? String(stat.value).trim() : '',
          description: typeof stat.description === 'string' ? stat.description.trim() : '',
        }))
        .filter((stat) => /^\d+-\d+$/.test(stat.value))
        .slice(0, 3);
    },
    showEnrichment() {
      return (this.isFinal || this.isLive) && (this.leadPerformer || this.previewStats.length);
    },
  },
  methods: {
    isWinner(side) {
      if (!this.isFinal || !this.game?.score?.winner) return false;
      const winner = this.game.score.winner;
      if (side === 'away') return winner === this.awayAbbr;
      if (side === 'home') return winner === this.homeAbbr;
      return false;
    },
    normalizeColor(value) {
      const fallback = '#0ea5e9';
      if (!value) return fallback;
      const clean = String(value).trim().replace('#', '');
      return /^[0-9a-fA-F]{6}$/.test(clean) ? `#${clean}` : fallback;
    },
  },
};
</script>

<style scoped>
.nhlc-card {
  background: #f8fbff;
  border: 1px solid #cbd5e1;
  border-top: 2px solid var(--winner-accent, #0ea5e9);
  border-radius: 14px;
  padding: 12px;
  font-family: 'Titillium Web', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.nhlc-card:hover { border-color: #94a3b8; box-shadow: 0 4px 14px rgba(14, 165, 233, 0.1); }

.nhlc-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.nhlc-context-badge {
  background: rgba(14, 165, 233, 0.1);
  color: #0c4a6e;
  border: 1px solid rgba(14, 165, 233, 0.3);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
}
.nhlc-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
}
.status--final { background: #e2e8f0; color: #475569; border: 1px solid #cbd5e1; }
.status--live { background: #dc2626; color: #fff; animation: pulse-badge 2s infinite; }
.status--pre { background: #e0f2fe; color: #0c4a6e; border: 1px solid #bae6fd; }

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.nhlc-live-dot {
  width: 5px;
  height: 5px;
  background: #fff;
  border-radius: 50%;
  animation: blink-dot 1.2s infinite;
}
@keyframes blink-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.1; }
}

.nhlc-score-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.nhlc-team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  border: 1px solid #dbe5ef;
  border-radius: 10px;
  padding: 8px;
  background: #ffffff;
  transition: opacity 0.2s;
}
.nhlc-team-row--winner {
  border-color: color-mix(in srgb, var(--winner-accent, #0ea5e9) 35%, #cbd5e1);
  box-shadow: inset 3px 0 0 color-mix(in srgb, var(--winner-accent, #0ea5e9) 65%, #ffffff);
}
.nhlc-team-row--loser { opacity: 0.56; }

.nhlc-team-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.nhlc-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}
.nhlc-logo-fallback {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dbe5ef;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

.nhlc-team-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.nhlc-team-name-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.nhlc-team-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nhlc-record {
  font-size: 10px;
  color: #64748b;
}
.nhlc-win-mark {
  color: #0369a1;
  font-size: 11px;
  font-weight: 900;
  flex-shrink: 0;
}

.nhlc-score {
  font-size: 24px;
  font-weight: 900;
  color: #1f2937;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
  flex-shrink: 0;
  line-height: 1;
}
.nhlc-score--winner { color: #111827; }

.nhlc-game-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}
.nhlc-tipoff {
  font-size: 11px;
  font-weight: 600;
  color: #0c4a6e;
}
.nhlc-clock {
  font-size: 11px;
  font-weight: 700;
  color: #dc2626;
}
.nhlc-broadcast {
  font-size: 11px;
  color: #64748b;
}
.nhlc-margin-badge {
  background: rgba(30, 41, 59, 0.08);
  color: #334155;
  border: 1px solid rgba(51, 65, 85, 0.25);
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
}
.nhlc-margin-badge--ot {
  background: rgba(14, 165, 233, 0.1);
  color: #0369a1;
  border-color: rgba(14, 165, 233, 0.3);
}
.nhlc-margin-badge--so {
  background: rgba(15, 23, 42, 0.12);
  color: #0f172a;
  border-color: rgba(15, 23, 42, 0.3);
}

.nhlc-series-strip {
  background: rgba(14, 165, 233, 0.08);
  border: 1px solid rgba(14, 165, 233, 0.2);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #0369a1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.nhlc-narrative {
  font-size: 12px;
  color: #475569;
  line-height: 1.45;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.nhlc-narrative i { color: #0ea5e9; margin-top: 2px; flex-shrink: 0; }
.nhlc-narrative--live {
  background: rgba(220, 38, 38, 0.06);
  border: 1px solid rgba(220, 38, 38, 0.15);
  border-radius: 8px;
  padding: 8px 10px;
}
.nhlc-narrative--live i { color: #dc2626; }

.nhlc-enrichment {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.nhlc-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #334155;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  padding: 3px 8px;
  line-height: 1.3;
  max-width: 100%;
}
.nhlc-pill strong {
  font-weight: 800;
  color: #0369a1;
}
.nhlc-pill--player i {
  color: #0ea5e9;
  font-size: 9px;
}
.nhlc-pill--stat {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #475569;
}

.nhlc-ctas {
  display: block;
  margin-top: 2px;
}
.nhlc-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: transform 0.12s, opacity 0.12s;
  text-decoration: none;
}
.nhlc-btn:hover { transform: translateY(-1px); opacity: 0.9; }
.nhlc-btn:focus-visible { outline: 2px solid #0ea5e9; outline-offset: 2px; }
.nhlc-btn--primary { background: #1d4ed8; color: #fff; }
.nhlc-btn--live { background: #dc2626; }
.nhlc-btn--live:hover { background: #b91c1c; }
</style>
